import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Link, useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import AuthPage from "../../components/auth/AuthPage";
import PasswordInput from "../../components/auth/PasswordInput";
import PasswordStrength from "../../components/auth/PasswordStrength";
import SubmitButton from "../../components/auth/SubmitButton";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../components/ui/form";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { API } from "../../constant/api";
import { PRIVACY_URL, TERMS_URL } from "../../constant/links";
import { useAuth } from "../../context/auth-context";
import { signUpSchema } from "../../utils/authSchemas";
import { getDeviceInfo } from "../../utils/device";
import { errorMessage } from "../../utils/errorMessage";
import sessionAxios from "../../utils/sessionAxios";

const COUNTRY_CODES: Record<string, string> = {
  Nigeria: "+234",
};

type SignUpValues = {
  firstName: string;
  lastName: string;
  email: string;
  country: string;
  phoneNumber: string;
  password: string;
  confirmPassword: string;
};

const capitalize = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();

const SignUp = () => {
  const { state } = useLocation();
  const { from = "/dashboard" } = state || {};
  const { isAuthenticated, authenticate } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const form = useForm<SignUpValues>({
    resolver: yupResolver(signUpSchema) as any,
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      country: "",
      phoneNumber: "",
      password: "",
      confirmPassword: "",
    },
    mode: "onTouched",
  });

  const password = form.watch("password");
  const country = form.watch("country");
  const countryCode = COUNTRY_CODES[country] ?? "";

  useEffect(() => {
    if (isAuthenticated) navigate(from);
  }, [from, isAuthenticated, navigate]);

  const onSubmit = (values: SignUpValues) => {
    if (loading) return;
    setLoading(true);

    sessionAxios
      .post(API.signup, {
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        email: values.email.trim(),
        password: values.password,
        country: values.country,
        phoneNumber: countryCode + values.phoneNumber,
        deviceInfo: getDeviceInfo(),
      })
      .then(() => authenticate())
      .then(() => navigate("/verify-mail"))
      .catch((e: any) => {
        setLoading(false);
        toast.error(
          errorMessage(
            e?.response?.data?.message || "An error occurred during signup"
          )
        );
      });
  };

  return (
    <AuthPage
      width="wide"
      title="Merchant's personal profile"
      description="Set up your personal profile. These credentials will enable you log in to the platform."
      footer={
        <span className="text-foreground">
          Have an account?{" "}
          <Link to="/login" className="font-semibold text-primary underline">
            Sign In
          </Link>
        </span>
      }
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
          <div className="grid grid-cols-1 gap-5 mds:grid-cols-2">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>First name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g. John"
                      autoComplete="given-name"
                      {...field}
                      onChange={(e) => field.onChange(capitalize(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g. Doe"
                      autoComplete="family-name"
                      {...field}
                      onChange={(e) => field.onChange(capitalize(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    autoComplete="email"
                    placeholder="example@domain.com"
                    {...field}
                    onChange={(e) => field.onChange(e.target.value.toLowerCase())}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 gap-5 mds:grid-cols-2">
            <FormField
              control={form.control}
              name="country"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Country of residence</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="h-[50px] rounded-xl bg-muted/50">
                        <SelectValue placeholder="Choose country" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="font-manrope">
                      {Object.keys(COUNTRY_CODES).map((name) => (
                        <SelectItem key={name} value={name}>
                          {name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phoneNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone number</FormLabel>
                  <div className="flex">
                    <span className="flex h-[50px] shrink-0 items-center rounded-l-xl border border-r-0 border-input bg-muted px-4 text-sm font-medium text-foreground">
                      {countryCode || "—"}
                    </span>
                    <FormControl>
                      <Input
                        type="tel"
                        inputMode="numeric"
                        autoComplete="tel-national"
                        placeholder="Enter phone number"
                        className="rounded-l-none"
                        disabled={!countryCode}
                        {...field}
                        onChange={(e) => {
                          // Strip a leading 0 so it isn't duplicated after the
                          // country code, and keep digits only.
                          const digits = e.target.value.replace(/\D/g, "");
                          field.onChange(digits.replace(/^0+/, ""));
                        }}
                      />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <PasswordInput
                    autoComplete="new-password"
                    placeholder="Create a password"
                    {...field}
                  />
                </FormControl>
                <PasswordStrength password={password} />
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Confirm password</FormLabel>
                <FormControl>
                  <PasswordInput
                    autoComplete="new-password"
                    placeholder="Re-enter your password"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <p className="text-center text-[12px] font-medium text-muted-foreground">
            By clicking "Sign up" you have read our{" "}
            <a
              href={PRIVACY_URL}
              target="_blank"
              rel="noreferrer"
              className="font-bold text-primary"
            >
              Privacy notice
            </a>
            , accept our{" "}
            <a
              href={TERMS_URL}
              target="_blank"
              rel="noreferrer"
              className="font-bold text-primary"
            >
              Terms and conditions
            </a>{" "}
            and you agree to it.
          </p>

          <SubmitButton loading={loading}>Sign Up</SubmitButton>
        </form>
      </Form>
    </AuthPage>
  );
};

export default SignUp;
